<div class="p-6 h-full gap-6 flex flex-col">
  @if (isLoading()) {
    <div class="max-w-7xl mx-auto w-full px-4 sm:px-6">
      <etxe-property-owner-details-skeleton />
    </div>
  } @else if (error()) {
    <div class="max-w-7xl mx-auto w-full px-4 sm:px-6">
      <div
        class="flex flex-col items-center justify-center p-12 border-2 border-dashed border-red-4 rounded-3xl gap-4 text-red-11 bg-red-1 min-h-[40vh]"
      >
        <mat-icon class="size-12 text-5xl! flex items-center justify-center text-red-9"
          >error_outline</mat-icon
        >
        <p class="text-base font-semibold text-center m-0">{{ error() }}</p>
        <a mat-flat-button class="primary rounded-lg! py-3! font-bold mt-2" routerLink="/properties">
          Retourner à mes biens
        </a>
      </div>
    </div>
  } @else if (property(); as prop) {
    <!-- 1. RÉCAPITULATIF DU BIEN -->
    <div class="flex flex-col gap-4 max-w-7xl mx-auto w-full px-4 sm:px-6">
      <!-- BANDEAU SYNTHÈSE DU BIEN + ONGLETS SEGMENTÉS -->
      <div
        class="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-2xl border border-neutral-4 surface-default dark:bg-neutral-2 shadow-xs"
      >
        <div class="flex items-center gap-3.5 min-w-0">
          <!-- Miniature Photo -->
          <div
            class="relative size-14 shrink-0 rounded-xl overflow-hidden bg-neutral-3 border border-neutral-4 flex items-center justify-center text-neutral-9 shadow-2xs"
          >
            @if (coverPhotoUrl(); as photo) {
              <img
                [src]="photo"
                [alt]="prop.presentation?.title || 'Photo du bien'"
                class="h-full w-full object-cover"
              />
            } @else {
              <mat-icon class="size-6 text-2xl! text-neutral-9">photo_camera</mat-icon>
            }
          </div>

          <!-- Titre & Localisation -->
          <div class="flex flex-col min-w-0">
            <h1 class="text-base sm:text-lg font-bold text-neutral-12 truncate m-0 leading-tight">
              {{ prop.presentation?.title || 'Votre bien immobilier' }}
            </h1>
            <div class="flex items-center gap-2 mt-1 text-xs text-neutral-10 truncate">
              <span class="flex items-center gap-1">
                <mat-icon class="size-3.5 text-[13px]! text-neutral-9">place</mat-icon>
                <span>{{ prop.location.address.city || 'Localisation' }}</span>
                @if (prop.location.address.zipCode) {
                  <span>({{ prop.location.address.zipCode }})</span>
                }
              </span>
              @if (prop.keyInfo.surface.habitable) {
                <span>•</span>
                <span>{{ prop.keyInfo.surface.habitable }} m²</span>
              }
            </div>
          </div>
        </div>

        <!-- ONGLETS SEGMENTÉS (Sentence case strictly applied) -->
        <div
          class="flex flex-wrap items-center p-1 rounded-xl bg-neutral-2 dark:bg-neutral-1 border border-neutral-4 self-start md:self-center gap-1"
        >
          <button
            type="button"
            (click)="setTab('property')"
            class="flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer"
            [class]="
              activeTab() === 'property'
                ? 'surface-default dark:bg-neutral-3 text-neutral-12 shadow-xs border border-neutral-4'
                : 'text-neutral-10 hover:text-neutral-12'
            "
          >
            <mat-icon
              class="size-4 text-base!"
              [class]="activeTab() === 'property' ? 'text-indigo-11' : 'text-neutral-9'"
              >home</mat-icon
            >
            <span>Fiche du bien</span>
          </button>
          <button
            type="button"
            (click)="setTab('applications')"
            class="flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer"
            [class]="
              activeTab() === 'applications'
                ? 'surface-default dark:bg-neutral-3 text-neutral-12 shadow-xs border border-neutral-4'
                : 'text-neutral-10 hover:text-neutral-12'
            "
          >
            <mat-icon
              class="size-4 text-base!"
              [class]="activeTab() === 'applications' ? 'text-indigo-11' : 'text-neutral-9'"
              >groups</mat-icon
            >
            <span>Candidatures reçues</span>
            <span
              class="inline-flex items-center px-1.5 py-0.2 rounded-md text-[10px] font-bold"
              [class]="
                activeTab() === 'applications' ? 'bg-indigo-3 text-indigo-11' : 'bg-neutral-4 text-neutral-10'
              "
            >
              {{ activeApplicationsCount() }}
            </span>
            @if (cancelledApplicationsCount() > 0) {
              <span
                class="inline-flex items-center px-1.5 py-0.2 rounded-md text-[10px] font-medium"
                [class]="
                  activeTab() === 'applications'
                    ? 'bg-neutral-3 text-neutral-11'
                    : 'bg-neutral-3 text-neutral-10'
                "
                [matTooltip]="
                  cancelledApplicationsCount() +
                  ' candidature' +
                  (cancelledApplicationsCount() > 1 ? 's' : '') +
                  ' annulée' +
                  (cancelledApplicationsCount() > 1 ? 's' : '')
                "
              >
                +{{ cancelledApplicationsCount() }} annulée{{ cancelledApplicationsCount() > 1 ? 's' : '' }}
              </span>
            }
          </button>

          @if (demoMode()) {
            <button
              type="button"
              (click)="setTab('agent-preview')"
              class="flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer"
              [class]="
                activeTab() === 'agent-preview'
                  ? 'surface-default dark:bg-neutral-3 text-neutral-12 shadow-xs border border-neutral-4'
                  : 'text-neutral-10 hover:text-neutral-12'
              "
            >
              <mat-icon
                class="size-4 text-base!"
                [class]="activeTab() === 'agent-preview' ? 'text-indigo-11' : 'text-neutral-9'"
                >visibility</mat-icon
              >
              <span>Aperçu vue agent</span>
            </button>
          }
        </div>
      </div>
    </div>

    <!-- 2. CONTENU PRINCIPAL SELON L'ONGLET -->
    @if (activeTab() === 'applications') {
      <div class="max-w-7xl mx-auto w-full px-4 sm:px-6">
        <etxe-property-applications-owner-comparison
          [property]="prop"
          [applications]="applications()"
          [isLoading]="isLoadingApplications()"
          (applicationSelected)="openDrawer($event)"
          (refresh)="refreshApplications.emit()"
        />
      </div>
    } @else if (activeTab() === 'agent-preview' && agentPreviewProperty(); as previewProp) {
      <div class="max-w-7xl mx-auto w-full px-4 sm:px-6 flex flex-col gap-6">
        <div
          class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 sm:p-5 rounded-3xl bg-indigo-a2 border border-indigo-a4 shadow-sm"
        >
          <div class="flex items-start sm:items-center gap-3.5">
            <div
              class="w-10 h-10 rounded-2xl bg-indigo-a3 text-indigo-11 flex items-center justify-center shrink-0 mt-0.5 sm:mt-0 shadow-2xs"
            >
              <mat-icon class="w-5! h-5! text-[20px]! flex items-center justify-center">visibility</mat-icon>
            </div>
            <div>
              <div class="flex items-center gap-2 flex-wrap">
                <h2 class="text-sm sm:text-base font-bold text-gray-12 m-0">
                  Aperçu fiche agent immobilier (sans mandat)
                </h2>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-indigo-a3 text-indigo-11 border border-indigo-a5"
                >
                  Mode simulation
                </span>
              </div>
              <p class="text-xs text-gray-11 m-0 mt-0.5 max-w-2xl">
                Voici exactement ce que les agents immobiliers certifiés voient sur leur console avant de
                candidater et d'obtenir votre mandat. L'adresse exacte, les coordonnées GPS précises, la
                section cadastrale et les documents du vendeur sont protégés et masqués.
              </p>
            </div>
          </div>

          <div class="flex items-center gap-2 shrink-0 w-full sm:w-auto justify-end">
            <button
              mat-flat-button
              type="button"
              (click)="setTab('property')"
              class="text-xs font-semibold py-2! px-4! primary shadow-sm"
            >
              <mat-icon class="text-xs! w-4! h-4!">arrow_back</mat-icon>
              <span>Voir la fiche complète</span>
            </button>
          </div>
        </div>

        <etxe-property-view
          [property]="previewProp"
          [isOwnerView]="false"
          [demoMode]="demoMode()"
          [showDefaultSidebar]="false"
        >
          <div view-sidebar class="flex flex-col gap-6">
            <etxe-property-candidacy-card
              [property]="previewProp"
              [maxCandidates]="5"
              [demoMode]="demoMode()"
              (apply)="demoAgentApply.emit($event)"
            />
          </div>
        </etxe-property-view>
      </div>
    } @else {
      <etxe-property-view
        [property]="prop"
        [isOwnerView]="true"
        [demoMode]="demoMode()"
        (previewAgentClicked)="setTab('agent-preview')"
        (propertyUpdated)="propertyUpdated.emit($event)"
      />
    }

    <!-- 3. TIROIR LATÉRAL (SLIDE-OVER) POUR LE DÉTAIL D'UNE CANDIDATURE -->
    <etxe-property-application-owner-drawer
      [isOpen]="isDrawerOpen()"
      [application]="selectedApplication()"
      [property]="prop"
      (closed)="closeDrawer()"
      (acceptRequested)="onAcceptApplication($event)"
      (rejectRequested)="onRejectApplication($event)"
    />
  }
</div>
